/**
 * 菜单 path 自动对上本端 views 下的 .vue。
 * 浏览器地址用菜单返回的 path 原样注册，不去掉 /admin、/manage、/customer。
 * 页面文件在 views 下没有这一层前缀，所以找组件时再试一次去掉前缀的路径。
 * 页面文件由构建配置按当前端扫进 virtual:app-views，这里只做 path 匹配。
 */
import type { RouteComponent } from "vue-router";

export interface RoutePageConfig {
    component: RouteComponent;
    /** 禁止用户固定页签 */
    noPin?: boolean;
    /**
     * 侧栏点击时拦截进页：同步打开外链。
     * 必须在函数开头同步 window.open，否则弹窗会被拦。
     */
    interceptClick?: () => void;
    /** 多路由共用同一 .vue 时指定 keep-alive 名 */
    keepAliveName?: string;
}

export interface ResolvedMenuPage extends RoutePageConfig {
    /** 注册到 Vue Router 的地址，与菜单 path 一致 */
    routerPath: string;
}

const byPath = new Map<string, RoutePageConfig>();
const byName = new Map<string, RoutePageConfig>();

const APP_PREFIX = /^\/(admin|manage|customer)(?=\/)/;

function normalizePath(raw: string): string {
    let p = String(raw || "")
        .trim()
        .replace(/\\/g, "/")
        .replace(/\.vue$/i, "");
    if (!p) return "";
    p = p.replace(/\/index$/i, "");
    if (!p.startsWith("/")) p = `/${p}`;
    p = p.replace(/\/+/g, "/");
    if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
    return p;
}

/** /customer/system/user → /system/user；没有端前缀则原样返回 */
export function stripAppPrefix(raw: string): string {
    const path = normalizePath(raw);
    const stripped = path.replace(APP_PREFIX, "");
    return stripped || "/";
}

function pascalRouteName(path: string): string {
    return path
        .replace(/^\//, "")
        .split("/")
        .filter(Boolean)
        .map(seg =>
            seg
                .split("-")
                .filter(Boolean)
                .map(part => part.charAt(0).toUpperCase() + part.slice(1))
                .join(""),
        )
        .join("");
}

function remember(path: string, config: RoutePageConfig, prefer: boolean): void {
    const key = normalizePath(path);
    if (!key) return;
    const prev = byPath.get(key);
    if (prev && !prefer) return;
    byPath.set(key, config);
    const name = pascalRouteName(key);
    if (name) byName.set(name, config);
}

function keysForViewFile(file: string): string[] {
    const normalized = file.replace(/\\/g, "/");
    const marker = "/views/";
    const at = normalized.lastIndexOf(marker);
    if (at < 0) return [];
    let rel = normalized.slice(at + marker.length).replace(/\.vue$/i, "");
    if (!rel || rel === "login") return [];
    rel = rel.replace(/^\/+/, "");
    const keys = [`/${rel}`];
    if (rel.endsWith("/index")) keys.push(`/${rel.slice(0, -"/index".length)}`);
    return keys.map(normalizePath).filter(Boolean);
}

/** 注册当前端 views 的懒加载表。由 virtual:app-views 在启动时调用。 */
export function registerViewModules(modules: Record<string, () => Promise<unknown>>, extras: Record<string, RoutePageConfig> = {}): void {
    byPath.clear();
    byName.clear();
    for (const [file, loader] of Object.entries(modules)) {
        const fromIndex = /\/index\.vue$/i.test(file.replace(/\\/g, "/"));
        const config: RoutePageConfig = { component: loader as RouteComponent };
        for (const key of keysForViewFile(file)) {
            remember(key, config, fromIndex);
        }
    }
    for (const [path, extra] of Object.entries(extras)) {
        const key = stripAppPrefix(path);
        const prev = byPath.get(key);
        remember(key, { ...prev, ...extra, component: extra.component || prev?.component }, true);
    }
}

/** 用菜单 path 找到页面。浏览器地址保留菜单 path，组件按 views 下的文件对。对不上返回 null。 */
export function resolveMenuPage(menuPath: string): ResolvedMenuPage | null {
    const full = normalizePath(menuPath);
    if (!full) return null;
    const filePath = stripAppPrefix(full);
    const candidates = [full, `${full}/index`, filePath, `${filePath}/index`];
    const seen = new Set<string>();
    for (const key of candidates) {
        const normalized = normalizePath(key);
        if (!normalized || seen.has(normalized)) continue;
        seen.add(normalized);
        const hit = byPath.get(normalized);
        if (hit?.component) {
            return { ...hit, routerPath: full };
        }
    }
    return null;
}

export function getRoutePageExtra(routeName: string | undefined): RoutePageConfig | undefined {
    const key = String(routeName || "").trim();
    if (!key || key.includes("/")) return undefined;
    return byName.get(key);
}

export function isRouteNoPin(routeName: string | undefined): boolean {
    return getRoutePageExtra(routeName)?.noPin === true;
}
